/*
  Ascend coaching booking funnel (the locked booking page).

  Renders into one mount element: <div data-coaching-funnel ...>. All config
  comes from data- attributes (API, product handles, image URLs, exit page) so
  the static test harness in dev/coaching-harness/ runs this exact file
  against the local backend. The step counter and progress line in the page's
  top bar are found by [data-apc-stepcount] / [data-apc-fill] when present.

  Contract (do not guess, read the backend):
    ascend-app/supabase/functions/coaching-public/core.ts        request shapes
    ascend-app/supabase/migrations/20261005200000_coaching_flexible_picks.sql
    ascend-app/supabase/functions/_shared/coaching-catalog.ts    SKUs + intake ids
  A pack is a number of sessions; the buyer places each one on an open date
  from {action:'calendar'} and holds them with {action:'hold', picks:[...]}.
  Variant ids are never hardcoded: they are resolved from /products/{handle}.js
  by SKU at runtime.

  The API's product id for The Ascend Standard is still "kitchen_sink" (handle
  the-kitchen-sink). Only the words a customer reads changed.

  Safety rule for this file: every string from the API or the buyer goes into
  the page through textContent / createTextNode. There is no innerHTML here.
*/
(function () {
  'use strict';

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var POLL_MS = 30000;
  var REQUEST_TIMEOUT_MS = 15000;

  /* Mirrors coaching-catalog.ts. SKUs only: prices always come from Shopify. */
  var CATALOG = {
    group: {
      title: 'Group',
      long: 'Group Speed Training',
      role: 'Small group',
      who: 'For the athlete who works harder with others on the line. Lowest cost per session.',
      handleKey: 'group',
      imgKey: 'group',
      imgAlt: 'A line of people standing together, arms over shoulders',
      packs: [
        { id: 'single', sku: 'APN-COACH-GROUP-1', sessions: 1, label: '1 session', detail: 'Try one.' },
        { id: 'week', sku: 'APN-COACH-GROUP-WEEK', sessions: 3, label: '3 sessions', detail: 'Place them on any three open dates.' },
        { id: 'month', sku: 'APN-COACH-GROUP-MONTH', sessions: 12, label: '12 sessions', detail: 'Place them on any twelve open dates.' }
      ]
    },
    private: {
      title: 'Private',
      long: 'Private Speed Training',
      role: 'One-on-one',
      who: 'For the athlete who wants every minute of the session on them.',
      handleKey: 'private',
      imgKey: 'private',
      imgAlt: 'Two athletes working one-on-one',
      packs: [
        { id: 'single', sku: 'APN-COACH-PRIVATE-1', sessions: 1, label: '1 session', detail: 'Try one.' },
        { id: 'week', sku: 'APN-COACH-PRIVATE-WEEK', sessions: 3, label: '3 sessions', detail: 'Place them on any three open dates.' },
        { id: 'month', sku: 'APN-COACH-PRIVATE-MONTH', sessions: 12, label: '12 sessions', detail: 'Place them on any twelve open dates.' }
      ]
    },
    kitchen_sink: {
      title: 'The Ascend Standard',
      long: 'The Ascend Standard',
      role: 'Everything',
      who: '18 private sessions plus a custom 6-week strength and conditioning program built for the athlete.',
      handleKey: 'kitchenSink',
      imgKey: 'standard',
      imgAlt: 'An athlete kneeling in the corner before a bout',
      packs: [
        { id: 'six_week', sku: 'APN-COACH-KITCHEN-SINK', sessions: 18, label: '18 private sessions', detail: 'Plus the custom program.' }
      ]
    }
  };
  var PRODUCT_ORDER = ['group', 'private', 'kitchen_sink'];
  var OPTION_ALIASES = { group: 'group', private: 'private', standard: 'kitchen_sink', kitchen_sink: 'kitchen_sink' };
  var PROGRAM_SKU = 'APN-COACH-SC-PROGRAM';
  var STACK_SKUS = { chocolate: 'APN-COACH-STACK-CHOC', vanilla: 'APN-COACH-STACK-VAN' };

  /* Ids MUST equal INTAKE_OPTIONS in coaching-catalog.ts. Labels are ours. */
  var INTAKE = {
    tier: [
      { id: 'rookie', label: 'Rookie', hint: 'New to structured training.' },
      { id: 'competitor', label: 'Competitor', hint: 'Has a base and competes.' },
      { id: 'elite', label: 'Elite', hint: 'Years of training. High level of competition.' },
      { id: 'champion', label: 'Champion', hint: 'Top of the sport.' }
    ],
    sport: [
      { id: 'football', label: 'Football' },
      { id: 'basketball', label: 'Basketball' },
      { id: 'baseball', label: 'Baseball' },
      { id: 'combat', label: 'Combat sports' },
      { id: 'running', label: 'Running' },
      { id: 'crossfit', label: 'CrossFit' }
    ],
    season: [
      { id: 'off-season', label: 'Off-season' },
      { id: 'pre-season', label: 'Pre-season' },
      { id: 'in-season', label: 'In-season' },
      { id: 'post-season', label: 'Post-season' }
    ],
    goal: [
      { id: 'speed', label: 'Speed' },
      { id: 'strength', label: 'Strength' },
      { id: 'power', label: 'Power' },
      { id: 'hypertrophy', label: 'Muscle size' },
      { id: 'strHyp', label: 'Strength and size' },
      { id: 'endurance', label: 'Muscular endurance' },
      { id: 'conditioning', label: 'Conditioning' }
    ],
    gap: [
      { id: 'recovery', label: 'Recovery' },
      { id: 'strength', label: 'Strength' },
      { id: 'power', label: 'Power' },
      { id: 'speed', label: 'Speed' },
      { id: 'conditioning', label: 'Conditioning' },
      { id: 'mobility', label: 'Mobility' },
      { id: 'movement', label: 'Movement quality' },
      { id: 'durability', label: 'Durability' }
    ]
  };

  /* One plain sentence per documented error code. */
  var ERRORS = {
    network: 'We could not reach the booking system. Check your connection and try again.',
    slow_down: 'Too many tries in a short time. Wait a few minutes, then try again.',
    booking_paused: 'Booking is paused right now. Check back soon.',
    unavailable: 'Something went wrong on our end. Try again in a minute.',
    slot_unavailable: 'One of those sessions is no longer offered. Pick another session.',
    bad_pick: 'One of those sessions did not send right. Pick your sessions again.',
    bad_start_date: 'That date did not work. Pick your sessions again.',
    start_too_far: 'One of those dates is too far out. Pick your sessions again.',
    athlete_name_required: 'Add the athlete\'s name.',
    athlete_age_required: 'Add the athlete\'s age.',
    guardian_required: 'Athletes under 18 need a parent or guardian name.',
    email_required: 'Add a valid email so we can send your confirmation.',
    unknown_pack: 'That option is not available. Go back and pick an option.',
    bad_hold_id: 'We lost track of your hold. Pick your sessions again.',
    too_large: 'That was too much text. Shorten your notes and try again.',
    generic: 'That did not go through. Reload the page and try again.'
  };
  var ATHLETE_ERRORS = { athlete_name_required: 1, athlete_age_required: 1, guardian_required: 1, email_required: 1 };

  /* ---------- small helpers ---------- */

  function el(tag, attrs, kids) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        var value = attrs[key];
        if (value === null || value === undefined || value === false) return;
        if (key === 'class') node.className = value;
        else if (key === 'text') node.textContent = value;
        else if (key.indexOf('on') === 0) node.addEventListener(key.slice(2), value);
        else if (value === true) node.setAttribute(key, '');
        else node.setAttribute(key, String(value));
      });
    }
    (kids || []).forEach(function (kid) {
      if (kid === null || kid === undefined || kid === false) return;
      node.appendChild(typeof kid === 'string' ? document.createTextNode(kid) : kid);
    });
    return node;
  }

  function money(cents) {
    var whole = cents % 100 === 0;
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
  }

  function fmtTime(hhmm) {
    var parts = String(hhmm || '').split(':');
    var h = parseInt(parts[0], 10);
    var m = parts[1] || '00';
    if (isNaN(h)) return String(hhmm || '');
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + ':' + m + ' ' + suffix;
  }

  /* Dates are 'YYYY-MM-DD' strings handled by hand, so no timezone can shift a day. */
  function parseIso(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return null;
    return { y: parseInt(m[1], 10), m: parseInt(m[2], 10) - 1, d: parseInt(m[3], 10) };
  }
  function toIso(utcDate) {
    return utcDate.getUTCFullYear() + '-' + String(utcDate.getUTCMonth() + 1).padStart(2, '0') + '-' + String(utcDate.getUTCDate()).padStart(2, '0');
  }
  function isoToUtc(iso) {
    var p = parseIso(iso);
    return p ? new Date(Date.UTC(p.y, p.m, p.d)) : null;
  }
  function addDays(iso, n) {
    var d = isoToUtc(iso);
    if (!d) return '';
    d.setUTCDate(d.getUTCDate() + n);
    return toIso(d);
  }
  function weekdayOf(iso) {
    var d = isoToUtc(iso);
    return d ? d.getUTCDay() : 0;
  }
  /* Monday that starts the week holding this date. */
  function mondayOf(iso) {
    var dow = weekdayOf(iso);
    return addDays(iso, dow === 0 ? -6 : 1 - dow);
  }
  function fmtDate(iso, withDay) {
    var p = parseIso(iso);
    if (!p) return '';
    var text = MONTHS[p.m] + ' ' + p.d;
    return withDay ? DAYS_SHORT[weekdayOf(iso)] + ' ' + text : text;
  }
  function fmtLong(iso) {
    var p = parseIso(iso);
    return p ? DAYS[weekdayOf(iso)] + ' ' + MONTHS[p.m] + ' ' + p.d : '';
  }

  function plural(n, word) { return n + ' ' + word + (n === 1 ? '' : 's'); }

  function joinNames(names) {
    if (names.length <= 1) return names.join('');
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  var uid = 0;
  function nextId(prefix) { uid += 1; return 'apc-' + prefix + '-' + uid; }

  /* Checked state as a class on the label. Chrome does not always repaint
     :has(input:checked) when another radio in the group takes the check. */
  function syncChecked(input) {
    var scope = input.form || document;
    var group = input.name ? scope.querySelectorAll('input[name="' + input.name + '"]') : [input];
    group.forEach(function (other) {
      var label = other.closest('label');
      if (label) label.classList.toggle('is-checked', other.checked);
    });
  }

  /* ---------- the funnel ---------- */

  function Funnel(mount) {
    var data = mount.dataset;
    var root = data.rootUrl || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';

    var cfg = {
      apiUrl: data.apiUrl || '',
      pageUrl: data.pageUrl || '/pages/coaching',
      contactUrl: data.contactUrl || '',
      root: root,
      handles: {
        group: data.handleGroup || '',
        private: data.handlePrivate || '',
        kitchenSink: data.handleKitchenSink || '',
        program: data.handleProgram || '',
        stack: data.handleStack || ''
      },
      images: {
        group: data.imgGroup || '',
        private: data.imgPrivate || '',
        standard: data.imgStandard || '',
        program: data.imgProgram || '',
        stack: data.imgStack || ''
      }
    };

    var state = {
      step: 'loading',
      busy: false,
      product: null,
      pack: null,
      athlete: { athlete_name: '', athlete_age: '', guardian_name: '', buyer_name: '', buyer_email: '', buyer_phone: '' },
      buyerNameTouched: false,
      cal: null,
      calKey: '',
      weeks: [],
      weekIndex: 0,
      picks: [],
      justFilled: {},
      wantsProgram: null,
      intake: { tier: '', sport: '', season: '', primaryGoal: '', secondaryGoal: '', gaps: [], notes: '' },
      hold: null,
      stack: 'none',
      discount: { code: '', left: 0 },
      variants: {}
    };

    var pollTimer = null;
    var clockTimer = null;

    /* Shell: the scrolling stage, the dock (alert + actions), a polite status region. */
    var stage = el('div', { class: 'apc-stage__inner' });
    var stageScroll = el('div', { class: 'apc-stage', 'data-apc-stage': true }, [stage]);
    var alertBox = el('div', { class: 'apc-alert', role: 'alert', hidden: true });
    var dockInfo = el('div', { class: 'apc-dock__info' });
    var dockActions = el('div', { class: 'apc-dock__actions' });
    var dock = el('div', { class: 'apc-dock' }, [alertBox, el('div', { class: 'apc-dock__row' }, [dockInfo, dockActions])]);
    var statusBox = el('div', { class: 'visually-hidden', role: 'status', 'aria-live': 'polite' });
    mount.replaceChildren(stageScroll, dock, statusBox);
    mount.setAttribute('data-ready', 'true');

    var barCount = document.querySelector('[data-apc-stepcount]');
    var barFill = document.querySelector('[data-apc-fill]');
    var barExit = document.querySelector('[data-apc-exit]');

    /* ---------- messaging ---------- */

    function showError(message) {
      alertBox.textContent = message || '';
      alertBox.hidden = !message;
    }
    function announce(message) {
      statusBox.textContent = '';
      window.setTimeout(function () { statusBox.textContent = message || ''; }, 30);
    }
    function errorFor(res) {
      var code = res && res.data && res.data.error;
      if (res && res.status === 0) return ERRORS.network;
      if (res && res.status === 429) return ERRORS.slow_down;
      if (res && res.status === 503 && code === 'booking_paused') return ERRORS.booking_paused;
      if (code && ERRORS[code]) return ERRORS[code];
      if (res && res.status >= 500) return ERRORS.unavailable;
      return ERRORS.generic;
    }

    /* ---------- network ---------- */

    function withTimeout(url, options) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = window.setTimeout(function () { if (controller) controller.abort(); }, REQUEST_TIMEOUT_MS);
      var opts = options || {};
      if (controller) opts.signal = controller.signal;
      return fetch(url, opts).then(
        function (response) { window.clearTimeout(timer); return response; },
        function (error) { window.clearTimeout(timer); throw error; }
      );
    }

    /* Always resolves: { status, data }. status 0 means the request never landed. */
    function api(body) {
      return withTimeout(cfg.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().then(
          function (json) { return { status: response.status, data: json && typeof json === 'object' ? json : {} }; },
          function () { return { status: response.status, data: {} }; }
        );
      }).catch(function () {
        return { status: 0, data: { error: 'network' } };
      });
    }

    function shopJson(path, body) {
      var options = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
      if (body) {
        options.method = 'POST';
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }
      return withTimeout(cfg.root + path, options).then(function (response) {
        return response.json().then(
          function (json) { return { ok: response.ok, data: json }; },
          function () { return { ok: false, data: null }; }
        );
      });
    }

    /* ---------- catalog: variant ids and prices by SKU ---------- */

    function loadCatalog() {
      var handles = [cfg.handles.group, cfg.handles.private, cfg.handles.kitchenSink, cfg.handles.program, cfg.handles.stack]
        .filter(Boolean);
      state.variants = {};
      return Promise.all(handles.map(function (handle) {
        return shopJson('products/' + encodeURIComponent(handle) + '.js')
          .then(function (res) { return res.ok ? res.data : null; })
          .catch(function () { return null; });
      })).then(function (products) {
        products.forEach(function (product) {
          if (!product || !Array.isArray(product.variants)) return;
          product.variants.forEach(function (variant) {
            if (!variant || !variant.sku || variant.available === false) return;
            var price = Number(variant.price);
            if (!variant.id || !isFinite(price)) return;
            state.variants[String(variant.sku).trim().toUpperCase()] = { id: variant.id, price: price };
          });
        });
      });
    }

    function variantFor(sku) { return state.variants[sku] || null; }
    function packsFor(product) {
      return CATALOG[product].packs.filter(function (pack) { return !!variantFor(pack.sku); });
    }
    function productOpen(product) { return packsFor(product).length > 0; }
    function currentPack() {
      if (!state.product || !state.pack) return null;
      var found = null;
      CATALOG[state.product].packs.forEach(function (pack) { if (pack.id === state.pack) found = pack; });
      return found;
    }
    function programOffered() { return !!variantFor(PROGRAM_SKU); }
    function stackFlavors() {
      return Object.keys(STACK_SKUS).filter(function (flavor) { return !!variantFor(STACK_SKUS[flavor]); });
    }
    function isStandard() { return state.product === 'kitchen_sink'; }
    function programInOrder() { return !isStandard() && state.wantsProgram === true && programOffered(); }
    function sessionsNeeded() {
      if (state.cal && state.cal.sessions_total) return Number(state.cal.sessions_total);
      var pack = currentPack();
      return pack ? pack.sessions : 1;
    }
    function maxPerDay() { return Math.max(1, Number(state.cal && state.cal.max_per_day) || 1); }

    /* ---------- step order ---------- */

    function steps() {
      var list = ['option'];
      if (!isStandard()) list.push('pack');
      list.push('athlete', 'times');
      /* The Standard always collects the intake. Group and Private see the
         program offer only when the program product can actually be sold. */
      if (isStandard() || programOffered()) list.push('program');
      if (isStandard() && stackFlavors().length) list.push('stack');
      list.push('review');
      return list;
    }
    function stepAfter(step) { var list = steps(); return list[list.indexOf(step) + 1]; }
    function stepBefore(step) { var list = steps(); return list[Math.max(0, list.indexOf(step) - 1)]; }

    /* ---------- busy state (double-submit guard) ---------- */

    function setBusy(on, button, label) {
      state.busy = on;
      mount.setAttribute('aria-busy', on ? 'true' : 'false');
      mount.querySelectorAll('button, input, select, textarea').forEach(function (control) {
        if (on) {
          if (!control.disabled) { control.disabled = true; control.setAttribute('data-apc-busy', '1'); }
        } else if (control.getAttribute('data-apc-busy')) {
          control.disabled = false;
          control.removeAttribute('data-apc-busy');
        }
      });
      if (button) {
        if (on) {
          button.setAttribute('data-apc-label', button.textContent);
          if (label) button.textContent = label;
        } else if (button.getAttribute('data-apc-label')) {
          button.textContent = button.getAttribute('data-apc-label');
          button.removeAttribute('data-apc-label');
        }
      }
    }

    /* ---------- render plumbing ---------- */

    function stopTimers() {
      if (pollTimer) { window.clearInterval(pollTimer); pollTimer = null; }
      if (clockTimer) { window.clearInterval(clockTimer); clockTimer = null; }
    }

    function go(step, options) {
      var opts = options || {};
      stopTimers();
      state.step = step;
      state.busy = false;
      mount.setAttribute('aria-busy', 'false');
      mount.setAttribute('data-step', step);
      showError(opts.error || '');
      dockInfo.replaceChildren();
      dockActions.replaceChildren();
      stage.replaceChildren();
      renderProgress();
      RENDER[step]();
      paintClock();
      stageScroll.scrollTop = 0;
      /* quiet = the page did this on load, not the buyer. Never steal focus then. */
      if (!opts.quiet) {
        var title = stage.querySelector('[data-apc-heading]');
        if (title) title.focus({ preventScroll: true });
      }
    }

    function renderProgress() {
      var list = steps();
      var index = list.indexOf(state.step);
      var text = index < 0 ? '' : 'Step ' + (index + 1) + ' of ' + list.length;
      var width = index < 0 ? '0%' : Math.round(((index + 1) / list.length) * 100) + '%';
      if (barCount) barCount.textContent = text;
      if (barFill) barFill.style.width = width;
    }

    /* "Group Speed Training, 12 sessions": what the buyer is booking, above every step after the pack. */
    function contextLine() {
      var pack = currentPack();
      if (!state.product || !pack || state.step === 'option' || state.step === 'pack') return null;
      return el('p', { class: 'apc-step__context', 'data-apc-context': true, text: CATALOG[state.product].long + ', ' + pack.label });
    }

    function heading(text, sub) {
      var nodes = [contextLine(), el('h1', { class: 'apc-step__title', tabindex: '-1', 'data-apc-heading': true, text: text })];
      if (sub) nodes.push(el('p', { class: 'apc-step__sub', text: sub }));
      return el('div', { class: 'apc-step__head' }, nodes);
    }

    function backButton(onBack, label) {
      return el('button', { type: 'button', class: 'apc-btn apc-btn--quiet', 'data-apc-back': true, onclick: onBack, text: label || 'Back' });
    }
    function nextButton(label, onNext, extraClass) {
      return el('button', { type: 'button', class: 'v3-btn v3-btn--solid apc-btn apc-btn--next' + (extraClass ? ' ' + extraClass : ''), 'data-apc-next': true, onclick: onNext, text: label });
    }
    function setDock(back, next, info) {
      dockActions.replaceChildren();
      if (back) dockActions.appendChild(back);
      if (next) dockActions.appendChild(next);
      dockInfo.replaceChildren();
      if (info) dockInfo.appendChild(info);
    }

    function discountNote() {
      if (!state.discount || !state.discount.code) return null;
      return el('p', { class: 'apc-deal', 'data-apc-discount': true, text: '10% off for the first 5, applied at checkout while it lasts.' });
    }
    function takeDiscount(payload) {
      if (payload && payload.discount && typeof payload.discount === 'object') {
        state.discount = { code: String(payload.discount.code || ''), left: Number(payload.discount.left) || 0 };
      }
    }

    function whereLine() {
      var cal = state.cal || {};
      var where = [cal.location_name, cal.address].filter(function (part) { return typeof part === 'string' && part.trim(); });
      if (!where.length) return null;
      return el('p', { class: 'apc-where', 'data-apc-where': true }, [
        el('span', { class: 'apc-where__label', text: 'Where' }),
        el('span', { text: where.join(', ') })
      ]);
    }

    function picture(src, alt, className) {
      if (!src) return null;
      return el('img', { class: className || 'apc-picture', src: src, alt: alt || '', loading: 'lazy' });
    }

    /* ---------- the hold clock (lives in the dock while a hold exists) ---------- */

    function paintClock() {
      if (!state.hold) return;
      var clock = el('span', { class: 'apc-clock__time', 'data-apc-clock': true });
      var clockSr = el('span', { class: 'visually-hidden', role: 'timer', 'aria-live': 'polite' });
      var wrap = el('p', { class: 'apc-clock' }, [el('span', { class: 'apc-clock__label', text: 'Seats held' }), clock, clockSr]);
      dockInfo.replaceChildren(wrap);

      var hold = state.hold;
      var holdMs = (Number(state.cal && state.cal.hold_minutes) || 15) * 60000;
      var remaining = Date.parse(hold.expiresAt) - hold.received;
      if (!isFinite(remaining) || remaining <= 0 || remaining > holdMs) remaining = holdMs;
      var deadline = hold.received + remaining;
      var lastSpoken = '';

      function tick() {
        var left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        var minutes = Math.floor(left / 60);
        var seconds = left % 60;
        clock.textContent = minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
        var spoken = '';
        if (left > 0 && seconds === 0) spoken = plural(minutes, 'minute') + ' left on your hold.';
        else if (left === 30) spoken = '30 seconds left on your hold.';
        if (!lastSpoken) spoken = plural(Math.max(1, Math.ceil(left / 60)), 'minute') + ' left on your hold.';
        if (spoken && spoken !== lastSpoken) { clockSr.textContent = spoken; lastSpoken = spoken; }
        if (left <= 0) expire();
      }
      function expire() {
        if (state.busy) return;
        stopTimers();
        state.hold = null;
        state.cal = null;
        state.calKey = '';
        go('times', { error: 'Your hold ran out, so those seats opened back up. Pick your sessions again.' });
      }
      tick();
      if (clockTimer) window.clearInterval(clockTimer);
      clockTimer = window.setInterval(tick, 500);
    }

    /* ---------- step: loading / closed ---------- */

    function renderLoading() {
      stage.appendChild(el('p', { class: 'apc-step__sub apc-step__loading', 'data-apc-heading': true, tabindex: '-1', text: 'Loading booking.' }));
    }

    function renderClosed() {
      stage.appendChild(heading('Booking is not open yet.', 'We could not load the training options. If you think this is a mistake, try again.'));
      if (cfg.contactUrl) {
        stage.appendChild(el('p', { class: 'apc-note' }, ['Need help? ', el('a', { href: cfg.contactUrl, text: 'Contact us' }), '.']));
      }
      setDock(null, nextButton('Try again', function (event) { boot(event.currentTarget); }));
    }

    /* ---------- step: option ---------- */

    function lowestPrice(product) {
      var prices = packsFor(product).map(function (pack) { return variantFor(pack.sku).price; });
      return prices.length ? Math.min.apply(null, prices) : null;
    }

    function pickProduct(product) {
      if (!CATALOG[product] || !productOpen(product)) return false;
      if (state.product !== product) {
        state.product = product;
        state.pack = product === 'kitchen_sink' ? 'six_week' : null;
        resetCalendar();
        state.wantsProgram = product === 'kitchen_sink' ? true : null;
        state.stack = 'none';
      }
      return true;
    }
    function resetCalendar() {
      state.picks = [];
      state.justFilled = {};
      state.cal = null;
      state.calKey = '';
      state.weeks = [];
      state.weekIndex = 0;
    }

    function renderOption() {
      stage.appendChild(heading('How do you want to train?', 'Pick one. You can change it any time before you pay.'));
      var list = el('div', { class: 'apc-options', role: 'group', 'aria-label': 'Training options' });
      PRODUCT_ORDER.forEach(function (product) {
        var entry = CATALOG[product];
        var open = productOpen(product);
        var low = lowestPrice(product);
        var single = entry.packs.length === 1;
        list.appendChild(el('button', {
          type: 'button',
          class: 'apc-option' + (product === 'kitchen_sink' ? ' apc-option--standard' : ''),
          'aria-pressed': state.product === product ? 'true' : 'false',
          disabled: !open,
          'data-apc-product': product,
          onclick: function () {
            if (state.busy || !pickProduct(product)) return;
            go(stepAfter('option'));
          }
        }, [
          picture(cfg.images[entry.imgKey], entry.imgAlt, 'apc-option__img'),
          el('span', { class: 'apc-option__body' }, [
            el('span', { class: 'v3-card__role', text: entry.role }),
            el('span', { class: 'apc-option__title', text: entry.title }),
            el('span', { class: 'apc-option__who', text: entry.who }),
            el('span', { class: 'apc-option__price', text: open ? (single ? money(low) : 'From ' + money(low)) : 'Not open yet' })
          ])
        ]));
      });
      stage.appendChild(list);
      var note = discountNote();
      if (note) stage.appendChild(note);
      setDock(null, null, el('p', { class: 'apc-dock__note', text: 'One-time purchase. No auto-renew.' }));
    }

    /* ---------- step: pack ---------- */

    function renderPack() {
      var entry = CATALOG[state.product];
      stage.appendChild(heading('How many sessions?', entry.long + '. You place each session on any open date.'));
      var list = el('div', { class: 'apc-packs', role: 'group', 'aria-label': 'Pack size' });
      entry.packs.forEach(function (pack) {
        var variant = variantFor(pack.sku);
        var perSession = variant && pack.sessions > 1 ? money(Math.round(variant.price / pack.sessions)) + ' a session' : '';
        list.appendChild(el('button', {
          type: 'button',
          class: 'apc-pack',
          'aria-pressed': state.pack === pack.id ? 'true' : 'false',
          disabled: !variant,
          'data-apc-pack': pack.id,
          onclick: function () {
            if (state.busy) return;
            if (state.pack !== pack.id) { state.pack = pack.id; resetCalendar(); }
            go(stepAfter('pack'));
          }
        }, [
          el('span', { class: 'apc-pack__body' }, [
            el('span', { class: 'apc-pack__title', text: pack.label }),
            el('span', { class: 'apc-pack__detail', text: pack.detail })
          ]),
          el('span', { class: 'apc-pack__side' }, [
            el('span', { class: 'apc-pack__price', text: variant ? money(variant.price) : 'Not open yet' }),
            perSession ? el('span', { class: 'apc-pack__note', text: perSession }) : null
          ])
        ]));
      });
      stage.appendChild(list);
      setDock(backButton(function () { go('option'); }), null, el('p', { class: 'apc-dock__note', text: 'One-time purchase. No auto-renew.' }));
    }

    /* ---------- step: athlete ---------- */

    function parseAge(value) {
      var text = String(value === null || value === undefined ? '' : value).trim();
      if (!/^\d{1,3}$/.test(text)) return null;
      return parseInt(text, 10);
    }

    function field(config) {
      var id = nextId(config.name);
      var hintId = config.hint ? id + '-hint' : null;
      var errorId = id + '-error';
      var input = el('input', {
        id: id,
        class: 'apc-input',
        name: config.name,
        type: config.type || 'text',
        maxlength: config.maxlength,
        autocomplete: config.autocomplete || 'off',
        inputmode: config.inputmode,
        required: !!config.required,
        'aria-describedby': (hintId ? hintId + ' ' : '') + errorId,
        'data-apc-field': config.name,
        oninput: function (event) { config.onInput(event.target.value, event.target); }
      });
      input.value = config.value || '';
      var kids = [el('label', { class: 'apc-label', for: id, text: config.label }), input];
      if (config.hint) kids.push(el('p', { class: 'apc-hint', id: hintId, text: config.hint }));
      kids.push(el('p', { class: 'apc-field__error', id: errorId, 'data-apc-error-for': config.name, hidden: true }));
      return el('div', { class: 'apc-field' + (config.half ? ' apc-field--half' : ''), 'data-apc-wrap': config.name }, kids);
    }

    function setFieldError(name, message) {
      var input = stage.querySelector('[data-apc-field="' + name + '"]');
      var slot = stage.querySelector('[data-apc-error-for="' + name + '"]');
      if (slot) { slot.textContent = message || ''; slot.hidden = !message; }
      if (input) {
        if (message) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }

    function athleteProblems() {
      var a = state.athlete;
      var problems = [];
      var age = parseAge(a.athlete_age);
      if (!a.athlete_name.trim()) problems.push(['athlete_name', 'Add the athlete\'s name.']);
      if (age === null) problems.push(['athlete_age', 'Add the athlete\'s age as a number.']);
      else if (age < 4 || age > 99) problems.push(['athlete_age', 'We cannot book that age online. Contact us and we will help.']);
      if (age !== null && age < 18 && !a.guardian_name.trim()) {
        problems.push(['guardian_name', 'Athletes under 18 need a parent or guardian name.']);
      }
      if (!a.buyer_name.trim()) problems.push(['buyer_name', 'Add your name.']);
      if (!EMAIL.test(a.buyer_email.trim())) problems.push(['buyer_email', 'Add a valid email so we can send your confirmation.']);
      if (a.buyer_phone.replace(/\D/g, '').length < 7) problems.push(['buyer_phone', 'Add a phone number your coach can reach.']);
      return problems;
    }

    function renderAthlete() {
      var a = state.athlete;
      stage.appendChild(heading('Who is training?', 'The athlete and the buyer can be two different people.'));

      var guardianWrap;
      function syncGuardian() {
        var age = parseAge(a.athlete_age);
        var minor = age !== null && age < 18;
        guardianWrap.hidden = !minor;
        guardianWrap.querySelector('input').required = minor;
        if (!minor) setFieldError('guardian_name', '');
      }
      /* Fill the buyer's name for them until they type their own. */
      function syncBuyerName() {
        if (state.buyerNameTouched) return;
        var age = parseAge(a.athlete_age);
        if (age === null) return;
        a.buyer_name = age < 18 ? a.guardian_name : a.athlete_name;
        var input = stage.querySelector('[data-apc-field="buyer_name"]');
        if (input) input.value = a.buyer_name;
      }

      var nameField = field({
        name: 'athlete_name', label: 'Athlete name', value: a.athlete_name, maxlength: 80, required: true, half: true,
        onInput: function (value) { a.athlete_name = value; setFieldError('athlete_name', ''); syncBuyerName(); }
      });
      var ageField = field({
        name: 'athlete_age', label: 'Athlete age', value: a.athlete_age, maxlength: 3, inputmode: 'numeric', required: true, half: true,
        onInput: function (value) { a.athlete_age = value; setFieldError('athlete_age', ''); syncGuardian(); syncBuyerName(); }
      });
      guardianWrap = field({
        name: 'guardian_name', label: 'Parent or guardian name', value: a.guardian_name, maxlength: 80,
        hint: 'A parent or guardian signs the waiver for anyone under 18.',
        onInput: function (value) { a.guardian_name = value; setFieldError('guardian_name', ''); syncBuyerName(); }
      });
      var buyerName = field({
        name: 'buyer_name', label: 'Your name', value: a.buyer_name, maxlength: 80, autocomplete: 'name', required: true,
        onInput: function (value) { a.buyer_name = value; state.buyerNameTouched = true; setFieldError('buyer_name', ''); }
      });
      var buyerEmail = field({
        name: 'buyer_email', label: 'Email', type: 'email', value: a.buyer_email, maxlength: 254, autocomplete: 'email', inputmode: 'email', required: true, half: true,
        hint: 'Your confirmation goes here.',
        onInput: function (value) { a.buyer_email = value; setFieldError('buyer_email', ''); }
      });
      var buyerPhone = field({
        name: 'buyer_phone', label: 'Phone', type: 'tel', value: a.buyer_phone, maxlength: 32, autocomplete: 'tel', inputmode: 'tel', required: true, half: true,
        hint: 'So your coach can reach you if a session moves.',
        onInput: function (value) { a.buyer_phone = value; setFieldError('buyer_phone', ''); }
      });

      function submit(event) {
        if (event) event.preventDefault();
        if (state.busy) return;
        var problems = athleteProblems();
        ['athlete_name', 'athlete_age', 'guardian_name', 'buyer_name', 'buyer_email', 'buyer_phone'].forEach(function (name) { setFieldError(name, ''); });
        if (problems.length) {
          problems.forEach(function (problem) { setFieldError(problem[0], problem[1]); });
          showError(problems.length === 1 ? problems[0][1] : 'Fix the ' + problems.length + ' fields marked above.');
          var first = stage.querySelector('[data-apc-field="' + problems[0][0] + '"]');
          if (first) first.focus();
          return;
        }
        go(stepAfter('athlete'));
      }

      var form = el('form', { class: 'apc-form', id: 'apc-athlete-form', novalidate: true, onsubmit: submit }, [
        el('fieldset', { class: 'apc-fieldset' }, [
          el('legend', { class: 'apc-legend', text: 'The athlete' }),
          el('div', { class: 'apc-fields' }, [nameField, ageField, guardianWrap])
        ]),
        el('fieldset', { class: 'apc-fieldset' }, [
          el('legend', { class: 'apc-legend', text: 'Who is booking' }),
          el('div', { class: 'apc-fields' }, [buyerName, buyerEmail, buyerPhone])
        ])
      ]);
      stage.appendChild(form);
      syncGuardian();
      /* A real submit button tied to the form, so Enter in any field submits it. */
      var next = el('button', { type: 'submit', form: 'apc-athlete-form', class: 'v3-btn v3-btn--solid apc-btn apc-btn--next', 'data-apc-next': true, text: 'Continue' });
      setDock(backButton(function () { go(stepBefore('athlete')); }), next);
    }

    /* ---------- step: times (the calendar picker) ---------- */

    function pickKey(slotId, date) { return slotId + '|' + date; }
    function isPicked(slotId, date) {
      return state.picks.some(function (pick) { return pick.slot_id === slotId && pick.date === date; });
    }
    function picksOn(date) {
      return state.picks.filter(function (pick) { return pick.date === date; }).length;
    }
    function dayFor(date) {
      var found = null;
      ((state.cal && state.cal.days) || []).forEach(function (day) { if (day.date === date) found = day; });
      return found;
    }
    function slotIn(day, slotId) {
      var found = null;
      ((day && day.slots) || []).forEach(function (slot) { if (slot.slot_id === slotId) found = slot; });
      return found;
    }
    function pickLabel(pick) { return fmtDate(pick.date, true) + ', ' + fmtTime(pick.start_time); }
    function sortPicks() {
      state.picks.sort(function (x, y) {
        return x.date < y.date ? -1 : x.date > y.date ? 1 : String(x.start_time).localeCompare(String(y.start_time));
      });
    }

    /* Group the calendar's days into Monday-start weeks. */
    function buildWeeks(cal) {
      var byMonday = {};
      var order = [];
      (cal.days || []).forEach(function (day) {
        if (!parseIso(day.date) || !Array.isArray(day.slots) || !day.slots.length) return;
        var monday = mondayOf(day.date);
        if (!byMonday[monday]) { byMonday[monday] = { start: monday, days: [] }; order.push(monday); }
        byMonday[monday].days.push(day);
      });
      order.sort();
      return order.map(function (monday, index) {
        var week = byMonday[monday];
        week.index = index;
        week.days.sort(function (x, y) { return x.date < y.date ? -1 : 1; });
        return week;
      });
    }

    /* Store a fresh calendar answer. Returns the picks it had to drop (gone or full). */
    function applyCalendar(payload) {
      state.cal = payload;
      state.calKey = state.product + '/' + state.pack;
      state.weeks = buildWeeks(payload);
      takeDiscount(payload);
      var lost = [];
      state.picks = state.picks.filter(function (pick) {
        var slot = slotIn(dayFor(pick.date), pick.slot_id);
        if (slot && slot.seats_left > 0) return true;
        state.justFilled[pickKey(pick.slot_id, pick.date)] = true;
        lost.push(pickLabel(pick));
        return false;
      });
      if (state.weekIndex >= state.weeks.length) state.weekIndex = Math.max(0, state.weeks.length - 1);
      return lost;
    }

    function fetchCalendar() {
      var key = state.product + '/' + state.pack;
      return api({ action: 'calendar', product: state.product, pack: state.pack }).then(function (res) {
        if (key !== state.product + '/' + state.pack) return { stale: true };
        if (res.status === 200 && res.data.ok && Array.isArray(res.data.days)) {
          return { ok: true, lost: applyCalendar(res.data) };
        }
        return { ok: false, error: errorFor(res) };
      });
    }

    function filledMessage(names) {
      if (!names.length) return 'One of your sessions just filled. Pick another session.';
      return joinNames(names) + ' just filled. Pick ' + (names.length === 1 ? 'another session.' : 'other sessions.');
    }
    function perDayLine() {
      var max = maxPerDay();
      return max === 1 ? 'One session a day. Pick another day.' : max + ' sessions a day at most. Pick another day.';
    }

    function renderTimes() {
      var key = state.product + '/' + state.pack;
      if (!state.cal || state.calKey !== key) {
        stage.appendChild(heading('Pick your sessions.', 'Loading the calendar.'));
        setDock(backButton(function () { go(stepBefore('times')); }), null);
        fetchCalendar().then(function (result) {
          if (state.step !== 'times' || result.stale) return;
          if (!result.ok) { renderTimesFailed(result.error); return; }
          go('times', { error: alertBox.textContent });
        });
        return;
      }

      var total = sessionsNeeded();
      var max = maxPerDay();
      stage.appendChild(heading(
        total === 1 ? 'Pick your session.' : 'Pick your ' + total + ' sessions.',
        (total === 1 ? 'Tap a time to pick it.' : 'Tap a time to add it. Tap again to remove it.') + ' ' + (max === 1 ? 'One session a day.' : 'Up to ' + max + ' sessions a day.')
      ));
      var where = whereLine();
      if (where) stage.appendChild(where);

      if (!state.weeks.length) {
        stage.appendChild(el('p', { class: 'apc-empty', text: 'No sessions are open for this option right now. Check back soon.' }));
        setDock(backButton(function () { go(stepBefore('times')); }), null);
        return;
      }

      var layout = el('div', { class: 'apc-times' });
      var calendar = el('div', { class: 'apc-calendar' });
      var chosen = el('section', { class: 'apc-chosen', 'aria-labelledby': 'apc-chosen-title' });
      layout.appendChild(calendar);
      layout.appendChild(chosen);
      stage.appendChild(layout);

      var weekNav = el('div', { class: 'apc-weeknav' });
      var strip = el('div', { class: 'apc-weekstrip', role: 'tablist', 'aria-label': 'Weeks' });
      var daysBox = el('div', { class: 'apc-days', 'data-apc-days': true });
      var helper = el('div', { class: 'apc-repeat' });
      var info = el('p', { class: 'apc-info', role: 'status', 'data-apc-info': true, hidden: true });
      calendar.appendChild(weekNav);
      calendar.appendChild(strip);
      calendar.appendChild(daysBox);
      calendar.appendChild(helper);
      calendar.appendChild(info);

      var counter = el('p', { class: 'apc-counter', 'data-apc-counter': true });
      var nextBtn = nextButton('Continue', proceed);
      setDock(backButton(function () { go(stepBefore('times')); }), nextBtn, counter);

      function setInfo(text) {
        info.textContent = text || '';
        info.hidden = !text;
      }

      function paintCounter() {
        var count = state.picks.length;
        counter.textContent = count + ' of ' + total + ' picked';
        counter.classList.toggle('is-complete', count === total);
        nextBtn.disabled = count !== total;
        nextBtn.setAttribute('aria-disabled', count !== total ? 'true' : 'false');
      }

      function paintWeekNav() {
        var week = state.weeks[state.weekIndex];
        weekNav.replaceChildren(
          el('button', {
            type: 'button', class: 'apc-weeknav__btn', 'aria-label': 'Previous week', 'data-apc-prev': true,
            disabled: state.weekIndex === 0,
            onclick: function () { if (state.weekIndex > 0) { state.weekIndex -= 1; paintAll(); } }
          }, [el('span', { 'aria-hidden': 'true', text: '←' })]),
          el('div', { class: 'apc-weeknav__label' }, [
            el('strong', { 'data-apc-week-title': true, text: 'Week of ' + fmtDate(week.start) }),
            el('span', { text: 'Week ' + (state.weekIndex + 1) + ' of ' + state.weeks.length })
          ]),
          el('button', {
            type: 'button', class: 'apc-weeknav__btn', 'aria-label': 'Next week', 'data-apc-next-week': true,
            disabled: state.weekIndex >= state.weeks.length - 1,
            onclick: function () { if (state.weekIndex < state.weeks.length - 1) { state.weekIndex += 1; paintAll(); } }
          }, [el('span', { 'aria-hidden': 'true', text: '→' })])
        );
        strip.replaceChildren();
        state.weeks.forEach(function (w) {
          var count = state.picks.filter(function (pick) { return mondayOf(pick.date) === w.start; }).length;
          var tab = el('button', {
            type: 'button', role: 'tab', class: 'apc-weekstrip__tab',
            'aria-selected': w.index === state.weekIndex ? 'true' : 'false',
            'data-apc-week': w.start,
            onclick: function () { state.weekIndex = w.index; paintAll(); }
          }, [
            el('span', { class: 'apc-weekstrip__date', text: fmtDate(w.start) }),
            count ? el('span', { class: 'apc-weekstrip__count', text: String(count), 'aria-label': plural(count, 'session') + ' picked' }) : null
          ]);
          strip.appendChild(tab);
        });
        var active = strip.querySelector('[aria-selected="true"]');
        if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
      }

      function toggle(day, slot) {
        if (state.busy) return;
        var keyStr = pickKey(slot.slot_id, day.date);
        showError('');
        setInfo('');
        if (isPicked(slot.slot_id, day.date)) {
          state.picks = state.picks.filter(function (pick) { return pickKey(pick.slot_id, pick.date) !== keyStr; });
          announce(pickLabel({ date: day.date, start_time: slot.start_time }) + ' removed. ' + state.picks.length + ' of ' + total + ' picked.');
        } else if (state.picks.length >= total) {
          showError('You have all ' + total + '. Remove one to change it.');
          return;
        } else if (picksOn(day.date) >= max) {
          showError(perDayLine());
          return;
        } else if (!(slot.seats_left > 0)) {
          return;
        } else {
          state.picks.push({ slot_id: slot.slot_id, date: day.date, start_time: slot.start_time, duration_minutes: slot.duration_minutes });
          sortPicks();
          announce(pickLabel({ date: day.date, start_time: slot.start_time }) + ' added. ' + state.picks.length + ' of ' + total + ' picked.');
        }
        paintAll(keyStr);
      }

      function paintDays(focusKey) {
        var week = state.weeks[state.weekIndex];
        daysBox.replaceChildren();
        week.days.forEach(function (day) {
          var dayId = nextId('day');
          var dayFull = picksOn(day.date) >= max;
          var list = el('div', { class: 'apc-slots', role: 'group', 'aria-labelledby': dayId });
          day.slots.forEach(function (slot) {
            var keyStr = pickKey(slot.slot_id, day.date);
            var picked = isPicked(slot.slot_id, day.date);
            var full = !(slot.seats_left > 0);
            var filled = !!state.justFilled[keyStr] && !picked;
            var seatText = full ? (filled ? 'Just filled' : 'Full') : plural(slot.seats_left, 'seat') + ' left';
            list.appendChild(el('button', {
              type: 'button',
              class: 'apc-slot' + (full ? ' is-full' : '') + (dayFull && !picked ? ' is-daymax' : ''),
              'aria-pressed': picked ? 'true' : 'false',
              disabled: full,
              'data-apc-slot': slot.slot_id,
              'data-apc-date': day.date,
              'data-seats-left': Math.max(0, Number(slot.seats_left) || 0),
              onclick: function () { toggle(day, slot); }
            }, [
              el('span', { class: 'visually-hidden', text: fmtLong(day.date) + ' ' }),
              el('span', { class: 'apc-slot__time', text: fmtTime(slot.start_time) }),
              el('span', { class: 'apc-slot__meta', text: slot.duration_minutes + ' min' }),
              el('span', { class: 'apc-slot__seats', text: seatText })
            ]));
          });
          daysBox.appendChild(el('section', { class: 'apc-day' + (dayFull ? ' is-daymax' : ''), 'data-apc-day': day.date }, [
            el('h2', { class: 'apc-day__name', id: dayId }, [
              el('span', { text: DAYS[weekdayOf(day.date)] }),
              el('span', { class: 'apc-day__date', text: fmtDate(day.date) })
            ]),
            list
          ]));
        });
        if (focusKey) {
          var parts = focusKey.split('|');
          var again = daysBox.querySelector('[data-apc-slot="' + parts[0] + '"][data-apc-date="' + parts[1] + '"]');
          if (again && !again.disabled) again.focus({ preventScroll: true });
        }
      }

      /* "Repeat these days every week": copy this week's picks forward until the pack is full. */
      function repeatPattern() {
        var week = state.weeks[state.weekIndex];
        var pattern = state.picks.filter(function (pick) { return mondayOf(pick.date) === week.start; });
        if (!pattern.length || state.picks.length >= total) return;
        var placed = 0;
        for (var w = state.weekIndex + 1; w < state.weeks.length && state.picks.length < total; w += 1) {
          var offset = (w - state.weekIndex) * 7;
          for (var p = 0; p < pattern.length && state.picks.length < total; p += 1) {
            var date = addDays(pattern[p].date, offset);
            var slot = slotIn(dayFor(date), pattern[p].slot_id);
            if (!slot || !(slot.seats_left > 0) || isPicked(slot.slot_id, date) || picksOn(date) >= max) continue;
            state.picks.push({ slot_id: slot.slot_id, date: date, start_time: slot.start_time, duration_minutes: slot.duration_minutes });
            placed += 1;
          }
        }
        sortPicks();
        showError('');
        var short = total - state.picks.length;
        var message = placed === 0
          ? 'No open dates on those days in the weeks ahead. Pick the rest by hand.'
          : 'Placed ' + plural(placed, 'session') + ' on those days in the weeks ahead. ' + state.picks.length + ' of ' + total + ' picked.' + (short > 0 ? ' Pick the last ' + short + ' by hand.' : '');
        paintAll();
        setInfo(message);
        announce(message);
      }

      function paintHelper() {
        helper.replaceChildren();
        var week = state.weeks[state.weekIndex];
        var inWeek = state.picks.filter(function (pick) { return mondayOf(pick.date) === week.start; });
        if (!inWeek.length || state.picks.length >= total || state.weekIndex >= state.weeks.length - 1 || total <= inWeek.length) return;
        helper.appendChild(el('button', {
          type: 'button', class: 'apc-repeat__btn', 'data-apc-repeat': true, onclick: repeatPattern,
          text: 'Repeat these days every week'
        }));
        helper.appendChild(el('span', { class: 'apc-repeat__hint', text: 'Fills the weeks ahead with the same days and times until you have all ' + total + '.' }));
      }

      function paintChosen() {
        chosen.replaceChildren(
          el('h2', { class: 'apc-chosen__title', id: 'apc-chosen-title', text: 'Your sessions (' + state.picks.length + ' of ' + total + ')' })
        );
        if (!state.picks.length) {
          chosen.appendChild(el('p', { class: 'apc-chosen__empty', text: total === 1 ? 'Nothing picked yet.' : 'Nothing picked yet. Pick your days in one week, then repeat them.' }));
          return;
        }
        var list = el('ol', { class: 'apc-chosen__list', 'data-apc-chosen': true });
        state.picks.forEach(function (pick) {
          list.appendChild(el('li', { class: 'apc-chosen__item' }, [
            el('span', { text: pickLabel(pick) }),
            el('button', {
              type: 'button', class: 'apc-chosen__remove', 'aria-label': 'Remove ' + pickLabel(pick),
              onclick: function () {
                var day = dayFor(pick.date);
                var slot = slotIn(day, pick.slot_id);
                if (day && slot) { toggle(day, slot); return; }
                state.picks = state.picks.filter(function (other) { return other !== pick; });
                paintAll();
              }
            }, [el('span', { 'aria-hidden': 'true', text: '×' })])
          ]));
        });
        chosen.appendChild(list);
      }

      function paintAll(focusKey) {
        paintWeekNav();
        paintDays(focusKey);
        paintHelper();
        paintChosen();
        paintCounter();
      }

      function refresh() {
        if (state.busy || state.step !== 'times') return;
        var active = document.activeElement;
        var focusKey = active && active.getAttribute && active.getAttribute('data-apc-slot')
          ? pickKey(active.getAttribute('data-apc-slot'), active.getAttribute('data-apc-date')) : null;
        fetchCalendar().then(function (result) {
          if (state.step !== 'times' || result.stale || !result.ok) return;
          if (result.lost.length) showError(filledMessage(result.lost));
          paintAll(focusKey);
        });
      }

      function proceed(event) {
        if (state.busy) return;
        var missing = total - state.picks.length;
        if (missing > 0) {
          showError('Pick ' + missing + ' more ' + (missing === 1 ? 'session' : 'sessions') + ' to continue.');
          return;
        }
        if (missing < 0) {
          showError('You have too many. Remove ' + (-missing) + ' to continue.');
          return;
        }
        var next = stepAfter('times');
        if (next === 'program') go('program');
        else createHold(event.currentTarget);
      }

      paintAll();
      pollTimer = window.setInterval(refresh, POLL_MS);
    }

    function renderTimesFailed(message) {
      showError(message);
      stage.replaceChildren(heading('Pick your sessions.', 'We could not load the calendar.'));
      setDock(
        backButton(function () { go(stepBefore('times')); }),
        nextButton('Try again', function () { resetCalendar(); go('times'); })
      );
      var title = stage.querySelector('[data-apc-heading]');
      if (title) title.focus({ preventScroll: true });
    }

    /* ---------- step: program (offer or intake) ---------- */

    function selectField(config) {
      var id = nextId(config.name);
      var select = el('select', {
        id: id, class: 'apc-input apc-select', name: config.name, required: !!config.required,
        'aria-describedby': id + '-error' + (config.hint ? ' ' + id + '-hint' : ''),
        'data-apc-field': config.name,
        onchange: function (event) { config.onChange(event.target.value); setFieldError(config.name, ''); }
      }, [el('option', { value: '', text: config.placeholder })].concat(config.options.map(function (option) {
        return el('option', { value: option.id, text: option.label });
      })));
      select.value = config.value || '';
      var kids = [el('label', { class: 'apc-label', for: id, text: config.label }), select];
      if (config.hint) kids.push(el('p', { class: 'apc-hint', id: id + '-hint', text: config.hint }));
      kids.push(el('p', { class: 'apc-field__error', id: id + '-error', 'data-apc-error-for': config.name, hidden: true }));
      return el('div', { class: 'apc-field apc-field--half' }, kids);
    }

    function intakePanel() {
      var intake = state.intake;
      var tierName = nextId('tier');
      var tierError = el('p', { class: 'apc-field__error', 'data-apc-error-for': 'tier', hidden: true });
      var tiers = el('fieldset', { class: 'apc-fieldset apc-fieldset--plain', 'data-apc-field': 'tier', tabindex: '-1' }, [
        el('legend', { class: 'apc-label', text: 'Training level' }),
        el('div', { class: 'apc-radios' }, INTAKE.tier.map(function (tier) {
          var id = nextId('tier-option');
          var input = el('input', {
            type: 'radio', id: id, name: tierName, value: tier.id, class: 'apc-radio__input', 'data-apc-tier': tier.id,
            onchange: function (event) { intake.tier = tier.id; setFieldError('tier', ''); syncChecked(event.target); }
          });
          input.checked = intake.tier === tier.id;
          return el('label', { class: 'apc-radio' + (input.checked ? ' is-checked' : ''), for: id }, [
            input,
            el('span', { class: 'apc-radio__text' }, [
              el('span', { class: 'apc-radio__title', text: tier.label }),
              el('span', { class: 'apc-radio__hint', text: tier.hint })
            ])
          ]);
        })),
        tierError
      ]);

      var gaps = el('fieldset', { class: 'apc-fieldset apc-fieldset--plain' }, [
        el('legend', { class: 'apc-label', text: 'Anything to work on? (optional)' }),
        el('div', { class: 'apc-checks' }, INTAKE.gap.map(function (gap) {
          var id = nextId('gap');
          var input = el('input', {
            type: 'checkbox', id: id, value: gap.id, class: 'apc-check__input', 'data-apc-gap': gap.id,
            onchange: function (event) {
              var at = intake.gaps.indexOf(gap.id);
              if (event.target.checked && at < 0) intake.gaps.push(gap.id);
              if (!event.target.checked && at >= 0) intake.gaps.splice(at, 1);
              syncChecked(event.target);
            }
          });
          input.checked = intake.gaps.indexOf(gap.id) >= 0;
          return el('label', { class: 'apc-check' + (input.checked ? ' is-checked' : ''), for: id }, [input, el('span', { text: gap.label })]);
        }))
      ]);

      var notesId = nextId('notes');
      var notes = el('textarea', {
        id: notesId, class: 'apc-input apc-textarea', rows: '3', maxlength: '500', name: 'notes', 'data-apc-field': 'notes',
        'aria-describedby': notesId + '-hint',
        oninput: function (event) { intake.notes = event.target.value; }
      });
      notes.value = intake.notes;

      return el('div', { class: 'apc-intake', 'data-apc-intake': true }, [
        tiers,
        el('div', { class: 'apc-fields' }, [
          selectField({
            name: 'sport', label: 'Main sport', placeholder: 'Choose a sport', options: INTAKE.sport, value: intake.sport, required: true,
            hint: 'Not listed? Pick the closest and tell Jesse in the notes.',
            onChange: function (value) { intake.sport = value; }
          }),
          selectField({
            name: 'season', label: 'Where is the season right now?', placeholder: 'Choose one', options: INTAKE.season, value: intake.season, required: true,
            onChange: function (value) { intake.season = value; }
          }),
          selectField({
            name: 'primaryGoal', label: 'Main goal', placeholder: 'Choose a goal', options: INTAKE.goal, value: intake.primaryGoal, required: true,
            onChange: function (value) { intake.primaryGoal = value; }
          }),
          selectField({
            name: 'secondaryGoal', label: 'Second goal (optional)', placeholder: 'None', options: INTAKE.goal, value: intake.secondaryGoal,
            onChange: function (value) { intake.secondaryGoal = value; }
          })
        ]),
        gaps,
        el('div', { class: 'apc-field' }, [
          el('label', { class: 'apc-label', for: notesId, text: 'Notes for Jesse (optional)' }),
          notes,
          el('p', { class: 'apc-hint', id: notesId + '-hint', text: 'Injuries, schedule, anything he should know. 500 characters max.' })
        ])
      ]);
    }

    function intakeProblems() {
      var intake = state.intake;
      var problems = [];
      if (!intake.tier) problems.push(['tier', 'Choose a training level.']);
      if (!intake.sport) problems.push(['sport', 'Choose a sport.']);
      if (!intake.season) problems.push(['season', 'Choose where the season is.']);
      if (!intake.primaryGoal) problems.push(['primaryGoal', 'Choose a main goal.']);
      return problems;
    }

    function cleanIntake() {
      var intake = state.intake;
      var out = { tier: intake.tier, sport: intake.sport, season: intake.season, primaryGoal: intake.primaryGoal };
      if (intake.secondaryGoal && intake.secondaryGoal !== intake.primaryGoal) out.secondaryGoal = intake.secondaryGoal;
      if (intake.gaps.length) out.gaps = intake.gaps.slice();
      var notes = intake.notes.trim().slice(0, 500);
      if (notes) out.notes = notes;
      return out;
    }

    function renderProgram() {
      var name = state.athlete.athlete_name.trim() || 'your athlete';
      var standard = isStandard();
      var program = variantFor(PROGRAM_SKU);
      var slotPanel = el('div', { class: 'apc-intake-slot' });

      function paintIntake() {
        slotPanel.replaceChildren();
        if (standard || state.wantsProgram === true) slotPanel.appendChild(intakePanel());
      }

      var intro = el('div', { class: 'apc-offer' }, [
        picture(cfg.images.program, 'Chalked hands gripping a barbell', 'apc-offer__img'),
        el('div', { class: 'apc-offer__body' })
      ]);
      var body = intro.querySelector('.apc-offer__body');

      if (standard) {
        stage.appendChild(heading(
          'Your program is included.',
          'The Ascend Standard comes with a custom 6-week strength and conditioning program. Tell Jesse about ' + name + ' so he can build it. He sends it as a PDF within 2 days.'
        ));
        stage.appendChild(intro);
        body.appendChild(el('ul', { class: 'apc-offer__list' }, [
          el('li', { text: 'Built by Jesse for ' + name + ' alone' }),
          el('li', { text: 'Sport, season, level and goals' }),
          el('li', { text: 'PDF within 2 days of purchase' })
        ]));
      } else {
        stage.appendChild(heading(
          'Add a custom strength program?',
          'A 6-week strength and conditioning program, built by Jesse for ' + name + '. He builds it around sport, season, level and goals, and sends it as a PDF within 2 days.'
        ));
        stage.appendChild(intro);
        var groupName = nextId('program');
        var choiceError = el('p', { class: 'apc-field__error', 'data-apc-error-for': 'wants_program', hidden: true });
        var radios = [
          { value: true, title: 'Yes, add the program', price: program ? money(program.price) : '' },
          { value: false, title: 'No thanks. Just the training.', price: '' }
        ].map(function (option) {
          var id = nextId('program-option');
          var input = el('input', {
            type: 'radio', id: id, name: groupName, class: 'apc-radio__input',
            value: option.value ? 'yes' : 'no', 'data-apc-program': option.value ? 'yes' : 'no',
            onchange: function (event) {
              state.wantsProgram = option.value;
              setFieldError('wants_program', '');
              showError('');
              syncChecked(event.target);
              paintIntake();
            }
          });
          input.checked = state.wantsProgram === option.value;
          return el('label', { class: 'apc-radio apc-radio--row' + (input.checked ? ' is-checked' : ''), for: id }, [
            input,
            el('span', { class: 'apc-radio__text' }, [el('span', { class: 'apc-radio__title', text: option.title })]),
            option.price ? el('span', { class: 'apc-radio__price', text: option.price }) : null
          ]);
        });
        body.appendChild(el('fieldset', { class: 'apc-fieldset apc-fieldset--plain', 'data-apc-field': 'wants_program', tabindex: '-1' }, [
          el('legend', { class: 'visually-hidden', text: 'Add the custom program' }),
          el('div', { class: 'apc-radios apc-radios--stack' }, radios),
          choiceError
        ]));
      }

      stage.appendChild(slotPanel);
      paintIntake();

      function submit(event) {
        if (state.busy) return;
        var problems = [];
        if (!standard && state.wantsProgram === null) problems.push(['wants_program', 'Choose yes or no to continue.']);
        if (standard || state.wantsProgram === true) problems = problems.concat(intakeProblems());
        ['wants_program', 'tier', 'sport', 'season', 'primaryGoal'].forEach(function (key) { setFieldError(key, ''); });
        if (problems.length) {
          problems.forEach(function (problem) { setFieldError(problem[0], problem[1]); });
          showError(problems.length === 1 ? problems[0][1] : 'Answer the ' + problems.length + ' questions marked above.');
          var first = stage.querySelector('[data-apc-field="' + problems[0][0] + '"]');
          if (first) first.focus();
          return;
        }
        createHold(event.currentTarget);
      }

      setDock(
        backButton(function () { go(stepBefore('program')); }),
        nextButton('Hold my sessions', submit),
        el('p', { class: 'apc-dock__note', text: 'Next we hold your seats while you check out. Nothing is charged yet.' })
      );
    }

    /* ---------- hold ---------- */

    function createHold(button) {
      var a = state.athlete;
      var age = parseAge(a.athlete_age);
      var athlete = {
        athlete_name: a.athlete_name.trim(),
        athlete_age: age,
        buyer_name: a.buyer_name.trim(),
        buyer_email: a.buyer_email.trim(),
        buyer_phone: a.buyer_phone.trim()
      };
      if (age !== null && age < 18) athlete.guardian_name = a.guardian_name.trim();
      var wantsProgram = isStandard() || programInOrder();
      var body = {
        action: 'hold',
        product: state.product,
        pack: state.pack,
        picks: state.picks.map(function (pick) { return { slot_id: pick.slot_id, date: pick.date }; }),
        athlete: athlete,
        wants_program: wantsProgram,
        intake: wantsProgram ? cleanIntake() : {}
      };

      setBusy(true, button, 'Holding your sessions...');
      api(body).then(function (res) {
        var payload = res.data || {};
        if (res.status === 200 && payload.ok && payload.hold_id && Array.isArray(payload.sessions)) {
          state.hold = {
            id: String(payload.hold_id),
            expiresAt: payload.expires_at,
            sessions: payload.sessions,
            received: Date.now()
          };
          state.justFilled = {};
          go(stepAfter('program') === 'stack' ? 'stack' : 'review');
          return;
        }
        var code = payload.error;
        if (code === 'slot_full') {
          var names = [];
          var firstDate = null;
          (Array.isArray(payload.full) ? payload.full : []).forEach(function (entry) {
            if (!entry || !entry.slot_id || !entry.date) return;
            var keyStr = pickKey(entry.slot_id, entry.date);
            state.justFilled[keyStr] = true;
            state.picks.filter(function (pick) { return pickKey(pick.slot_id, pick.date) === keyStr; })
              .forEach(function (pick) { names.push(pickLabel(pick)); });
            state.picks = state.picks.filter(function (pick) { return pickKey(pick.slot_id, pick.date) !== keyStr; });
            if (!firstDate || entry.date < firstDate) firstDate = entry.date;
          });
          var message = filledMessage(names);
          fetchCalendar().then(function () {
            if (firstDate) {
              var monday = mondayOf(firstDate);
              state.weeks.forEach(function (w) { if (w.start === monday) state.weekIndex = w.index; });
            }
            go('times', { error: message });
          });
          return;
        }
        if (code === 'slot_unavailable') {
          var gone = payload.pick && payload.pick.slot_id ? state.picks.filter(function (pick) {
            return pick.slot_id === payload.pick.slot_id && pick.date === payload.pick.date;
          }) : [];
          state.picks = state.picks.filter(function (pick) { return gone.indexOf(pick) < 0; });
          var text = gone.length ? pickLabel(gone[0]) + ' is no longer offered. Pick another session.' : ERRORS.slot_unavailable;
          fetchCalendar().then(function () { go('times', { error: text }); });
          return;
        }
        if (code === 'wrong_session_count') {
          var want = Number(payload.sessions_total) || sessionsNeeded();
          go('times', { error: 'This pack is ' + plural(want, 'session') + '. Pick exactly ' + want + '.' });
          return;
        }
        if (code === 'too_many_on_one_day') {
          var cap = Number(payload.max_per_day) || maxPerDay();
          go('times', { error: cap === 1 ? 'One session a day. Pick another day for one of them.' : cap + ' sessions a day at most. Spread them out.' });
          return;
        }
        if (code === 'bad_pick' || code === 'bad_start_date' || code === 'start_too_far') {
          state.picks = [];
          fetchCalendar().then(function () { go('times', { error: ERRORS[code] }); });
          return;
        }
        if (code && ATHLETE_ERRORS[code]) {
          go('athlete', { error: ERRORS[code] });
          return;
        }
        setBusy(false, button);
        showError(errorFor(res));
      });
    }

    function releaseHold(keepalive) {
      var hold = state.hold;
      state.hold = null;
      if (!hold) return Promise.resolve();
      if (keepalive) {
        try {
          fetch(cfg.apiUrl, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'release', hold_id: hold.id }) }).catch(function () {});
        } catch (error) { /* leaving the page anyway */ }
        return Promise.resolve();
      }
      return api({ action: 'release', hold_id: hold.id });
    }

    function timesProperty() {
      var sessions = (state.hold && state.hold.sessions) || [];
      return sessions.map(function (session) { return String(session.label || ''); }).filter(Boolean).join('; ');
    }

    function orderLines() {
      var pack = currentPack();
      var lines = [];
      var packVariant = pack ? variantFor(pack.sku) : null;
      if (packVariant) {
        lines.push({
          key: 'pack',
          title: CATALOG[state.product].long + (isStandard() ? '' : ', ' + pack.label),
          price: packVariant.price,
          id: packVariant.id
        });
      }
      if (programInOrder()) {
        var program = variantFor(PROGRAM_SKU);
        lines.push({ key: 'program', title: 'Custom 6-week strength and conditioning program', price: program.price, id: program.id });
      }
      if (isStandard() && state.stack !== 'none' && variantFor(STACK_SKUS[state.stack])) {
        var stack = variantFor(STACK_SKUS[state.stack]);
        lines.push({
          key: 'stack',
          title: 'Supplement stack, ' + (state.stack === 'chocolate' ? 'Chocolate' : 'Vanilla'),
          price: stack.price,
          id: stack.id
        });
      }
      return lines;
    }

    /* ---------- step: stack (The Ascend Standard only, after the hold) ---------- */

    function renderStack() {
      if (!state.hold) { go('times'); return; }
      var flavors = stackFlavors();
      if (!flavors.length) { go('review'); return; }
      var stackPrice = variantFor(STACK_SKUS[flavors[0]]).price;
      stage.appendChild(heading('Add the supplement stack?', 'Replenish Plus, Resilience and Fortify in one stack. It ships to you. Shipping is figured at checkout.'));
      var offer = el('div', { class: 'apc-offer apc-offer--stack' }, [
        picture(cfg.images.stack, 'Ascend supplement tubs on a gym bench. This stack is Replenish Plus, Resilience and Fortify.', 'apc-offer__img apc-offer__img--color'),
        el('div', { class: 'apc-offer__body' })
      ]);
      var body = offer.querySelector('.apc-offer__body');
      var stackName = nextId('stack');
      var options = flavors.map(function (flavor) {
        return { value: flavor, title: 'Yes, ' + (flavor === 'chocolate' ? 'Chocolate' : 'Vanilla'), price: money(stackPrice) };
      }).concat([{ value: 'none', title: 'No stack. Just the training.', price: '' }]);
      if (options.every(function (option) { return option.value !== state.stack; })) state.stack = 'none';
      body.appendChild(el('fieldset', { class: 'apc-fieldset apc-fieldset--plain', 'data-apc-stack': true }, [
        el('legend', { class: 'visually-hidden', text: 'Supplement stack' }),
        el('div', { class: 'apc-radios apc-radios--stack' }, options.map(function (option) {
          var id = nextId('stack-option');
          var input = el('input', {
            type: 'radio', id: id, name: stackName, class: 'apc-radio__input', value: option.value, 'data-apc-stack-option': option.value,
            onchange: function (event) { state.stack = option.value; syncChecked(event.target); }
          });
          input.checked = state.stack === option.value;
          return el('label', { class: 'apc-radio apc-radio--row' + (input.checked ? ' is-checked' : ''), for: id }, [
            input,
            el('span', { class: 'apc-radio__text' }, [el('span', { class: 'apc-radio__title', text: option.title })]),
            option.price ? el('span', { class: 'apc-radio__price', text: option.price }) : null
          ]);
        }))
      ]));
      stage.appendChild(offer);
      setDock(
        backButton(function () { state.stack = 'none'; go('review'); }, 'Skip'),
        nextButton('Continue', function () { go('review'); })
      );
    }

    /* ---------- step: review ---------- */

    function renderReview() {
      var hold = state.hold;
      if (!hold) { go('times'); return; }
      var name = state.athlete.athlete_name.trim();
      stage.appendChild(heading('Your sessions are held.', 'Finish checkout before the clock runs out and they are yours.'));

      var sessionList = el('ol', { class: 'apc-sessions', 'data-apc-sessions': true }, hold.sessions.map(function (session) {
        return el('li', { class: 'apc-sessions__item', text: String(session.label || session.date || '') });
      }));
      var layout = el('div', { class: 'apc-review' });
      layout.appendChild(el('section', { class: 'apc-block', 'aria-label': 'Your sessions' }, [
        el('h2', { class: 'apc-block__title', text: plural(hold.sessions.length, 'session') + (name ? ' for ' + name : '') }),
        sessionList,
        whereLine()
      ]));

      var lines = orderLines();
      var total = lines.reduce(function (sum, line) { return sum + line.price; }, 0);
      var summary = el('section', { class: 'apc-summary', 'data-apc-summary': true, 'aria-label': 'Your order' }, [
        el('h2', { class: 'apc-block__title', text: 'Your order' }),
        el('dl', { class: 'apc-summary__lines' }, lines.reduce(function (nodes, line) {
          nodes.push(el('dt', { text: line.title }));
          nodes.push(el('dd', { text: money(line.price) }));
          return nodes;
        }, []).concat([
          el('dt', { class: 'apc-summary__total', text: 'Total before tax' }),
          el('dd', { class: 'apc-summary__total', 'data-apc-total': true, text: money(total) })
        ]))
      ]);
      if (isStandard()) summary.appendChild(el('p', { class: 'apc-hint', text: 'The custom strength and conditioning program is included.' }));
      var note = discountNote();
      if (note) summary.appendChild(note);
      summary.appendChild(el('p', { class: 'apc-hint', text: 'One-time purchase. No auto-renew. The liability waiver is signed on paper at the first session.' }));
      layout.appendChild(summary);
      stage.appendChild(layout);

      setDock(
        backButton(function (event) {
          if (state.busy) return;
          setBusy(true, event.currentTarget, 'Releasing...');
          stopTimers();
          releaseHold().then(function () {
            state.cal = null;
            state.calKey = '';
            go('times');
          });
        }, 'Change my sessions'),
        nextButton('Go to checkout', function (event) { checkout(event.currentTarget); }, 'apc-btn--checkout')
      );
    }

    /* ---------- checkout handoff ---------- */

    /* Drop every cart line a past attempt of this funnel left behind. Nothing else is touched. */
    function removeHeldLines() {
      return shopJson('cart.js').then(function (res) {
        var items = (res.ok && res.data && Array.isArray(res.data.items)) ? res.data.items : [];
        var stale = items.filter(function (item) {
          return item && item.properties && typeof item.properties === 'object' && item.properties._hold;
        });
        return stale.reduce(function (chain, item) {
          return chain.then(function () {
            return shopJson('cart/change.js', { id: item.key, quantity: 0 }).then(function (changed) {
              if (!changed.ok) throw new Error('cart_change_failed');
            });
          });
        }, Promise.resolve());
      });
    }

    function fireAddToCart(lines) {
      var value = lines.reduce(function (sum, line) { return sum + line.price; }, 0) / 100;
      var name = lines.length ? lines[0].title : 'Ascend coaching';
      try {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'AddToCart', {
            content_name: name,
            value: value,
            currency: 'USD',
            content_ids: lines.map(function (line) { return String(line.id); }),
            contents: lines.map(function (line) { return { id: String(line.id), quantity: 1 }; })
          });
        }
      } catch (error) { /* tracking must never block checkout */ }
      try {
        if (typeof window.ttq !== 'undefined' && window.ttq && typeof window.ttq.track === 'function') {
          window.ttq.track('AddToCart', { content_name: name, value: value, currency: 'USD' });
        }
      } catch (error) { /* same */ }
    }

    function checkout(button) {
      if (state.busy || !state.hold) return;
      var hold = state.hold;
      var lines = orderLines();
      if (!lines.length || lines[0].key !== 'pack') {
        showError('We could not find that training option in the store. Reload the page and try again.');
        return;
      }
      var athleteName = state.athlete.athlete_name.trim();
      var items = lines.map(function (line) {
        /* Every line this funnel adds carries _hold, so a later attempt can clear all of them. */
        var properties = { _hold: hold.id, Athlete: athleteName };
        if (line.key === 'pack') properties.Times = timesProperty();
        return { id: line.id, quantity: 1, properties: properties };
      });

      setBusy(true, button, 'Sending you to checkout...');
      showError('');

      removeHeldLines()
        .then(function () { return shopJson('cart/add.js', { items: items }); })
        .then(function (added) {
          if (!added.ok) throw new Error('cart_add_failed');
          return api({ action: 'extend', hold_id: hold.id });
        })
        .then(function (extended) {
          /* ok:false with a 200 means the hold is gone. A network miss is not proof of that. */
          if (extended.status === 200 && extended.data && extended.data.ok === false) {
            return removeHeldLines().catch(function () {}).then(function () {
              state.hold = null;
              state.cal = null;
              state.calKey = '';
              go('times', { error: 'Your hold ran out before checkout, so those seats opened back up. Pick your sessions again.' });
              return null;
            });
          }
          fireAddToCart(lines);
          var code = state.discount && state.discount.code;
          var target = code
            ? cfg.root + 'discount/' + encodeURIComponent(code) + '?redirect=' + cfg.root + 'checkout'
            : cfg.root + 'checkout';
          window.location.href = target;
          return null;
        })
        .catch(function () {
          /* The hold is still running, so redraw the step and keep the clock going. */
          go('review', { error: 'We could not add your training to the cart. Check your connection and try again.' });
        });
    }

    var RENDER = {
      loading: renderLoading,
      closed: renderClosed,
      option: renderOption,
      pack: renderPack,
      athlete: renderAthlete,
      times: renderTimes,
      program: renderProgram,
      stack: renderStack,
      review: renderReview
    };

    /* ---------- boot ---------- */

    function boot(button) {
      if (state.busy) return;
      if (button) setBusy(true, button, 'Loading...');
      loadCatalog().then(function () {
        state.busy = false;
        var anyOpen = PRODUCT_ORDER.some(productOpen);
        if (!cfg.apiUrl || !anyOpen) { go('closed', { quiet: !button }); return; }
        var wanted = null;
        try { wanted = new URLSearchParams(window.location.search).get('option'); } catch (error) { wanted = null; }
        var product = wanted && OPTION_ALIASES[wanted];
        if (product && pickProduct(product)) go(stepAfter('option'), { quiet: !button });
        else go('option', { quiet: !button });
        /* Ask once up front so the first-5 note can show before the buyer reaches the calendar. */
        api({ action: 'calendar', product: 'group', pack: 'single' }).then(function (res) {
          if (res.status !== 200 || !res.data.ok) return;
          takeDiscount(res.data);
          if (state.step === 'option' && state.discount.code && !stage.querySelector('[data-apc-discount]')) {
            var note = discountNote();
            if (note) stage.appendChild(note);
          }
        });
      });
    }

    /* Leaving through the exit link frees the seats instead of letting the hold sit for 15 minutes. */
    if (barExit) {
      barExit.addEventListener('click', function () { if (state.hold) releaseHold(true); });
    }

    /* Back from checkout through the browser cache: wake the buttons up again. */
    window.addEventListener('pageshow', function (event) {
      if (event.persisted && state.step === 'review') go('review');
    });

    go('loading', { quiet: true });
    boot();
  }

  function init() {
    document.querySelectorAll('[data-coaching-funnel]').forEach(function (mount) {
      if (mount.getAttribute('data-ready')) return;
      Funnel(mount);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
